import { useMergeState } from '@/shared/lib/customHooks';
import { showMessageError } from '@/shared/lib';
import _ from 'lodash';
import { useEffect } from 'react';
import { t } from 'ttag';
import { checkAddPhysicianInterpretation } from '../../lib/editReportModal';
import type { EditReportModalProps, EditReportModalState } from './editReportModal.types';

/**
 * Drives the edit report modal where a physician adds an interpretation comment or a signature to
 * an inbox report. Seeds the interpretation and signature from props and resets them each time
 * the modal opens or closes, switches to comment mode for the interpretation and findings
 * headers, and exposes the input, signature and submit handlers.
 *
 * @param props - Initial interpretation and signature, modal header, report format and kind flags, profile, submit callbacks and open flag.
 * @returns An object carrying the modal state and its interpretation, signature and submit handlers.
 */
const useEditReportModal = ({
  physicianInterpretation,
  signature,
  header,
  isNewFormat,
  formatVersion,
  isUploadedAiReport,
  isUploadedArcsReport,
  isInstantReport,
  isEventReport,
  isNotificationReport,
  isInterimReport,
  profile,
  handleClickAddComment,
  handleClickAddSignature,
  isOpen,
  isNewNotiEventReport,
}: Pick<
  EditReportModalProps,
  | 'physicianInterpretation'
  | 'signature'
  | 'header'
  | 'isNewFormat'
  | 'formatVersion'
  | 'isUploadedAiReport'
  | 'isUploadedArcsReport'
  | 'isInstantReport'
  | 'isEventReport'
  | 'isNotificationReport'
  | 'isInterimReport'
  | 'profile'
  | 'handleClickAddComment'
  | 'handleClickAddSignature'
  | 'isOpen'
  | 'isNewNotiEventReport'
>) => {
  const [state, setState] = useMergeState<EditReportModalState>({
    physicianInterpretation,
    signature,
    isCommentInput: header === 'Physician interpretation' || header === 'Update findings',
  });

  /**
   * Validates and stores edits to the physician interpretation text of an inbox report. Only when the text grew does it check the remaining line budget for the report's format and type, covering AI, ARCS, instant, event, notification, interim and new notification-event reports, rejecting the edit with a could-not-add-more-lines error and otherwise saving the new text.
   *
   * @param name - Field name reported by the input.
   * @param value - New interpretation text.
   * @returns Nothing.
   */
  const onChangePhysicianInterpretation = (name: string, value: string) => {
    let overflow = false;
    if (value.length > state.physicianInterpretation.length) {
      overflow = checkAddPhysicianInterpretation({
        value,
        isNewFormat,
        formatVersion,
        isUploadedAiReport,
        isUploadedArcsReport,
        isInstantReport,
        isEventReport,
        isNotificationReport,
        isInterimReport,
        profile,
        isNewNotiEventReport,
      });
    }
    if (overflow) {
      showMessageError({ content: t`Could not add more lines` });
    } else {
      setState({ physicianInterpretation: value });
    }
  };

  /**
   * Captures the physician signature drawn on the edit report modal pad. Stores the base64 image string in the signature state.
   *
   * @param signatureBase64String - Base64 encoded image of the drawn signature.
   * @returns Nothing.
   */
  const onEndSignature = (signatureBase64String: string) => {
    setState({ signature: signatureBase64String });
  };

  /**
   * Submits the edit report modal as either a comment or a signature. In comment mode it re-checks the interpretation against the report's line budget, showing a could-not-add-more-lines error on overflow and otherwise adding the trimmed comment, while in signature mode it submits the captured signature together with the interpretation.
   *
   * @returns Nothing.
   */
  const handleClick = () => {
    if (state.isCommentInput) {
      const overflow = checkAddPhysicianInterpretation({
        value: state.physicianInterpretation,
        isNewFormat,
        formatVersion,
        isUploadedAiReport,
        isUploadedArcsReport,
        isInstantReport,
        isEventReport,
        isNotificationReport,
        isInterimReport,
        profile,
        isNewNotiEventReport,
      });
      if (overflow) {
        showMessageError({ content: t`Could not add more lines` });
      } else {
        const trimmedValue = _.trim(state.physicianInterpretation);
        handleClickAddComment(trimmedValue);
      }
    } else {
      handleClickAddSignature(state.signature, state.physicianInterpretation);
    }
  };

  useEffect(() => {
    setState({
      isCommentInput: header === 'Physician interpretation' || header === 'Update findings',
    });
  }, [header]);

  useEffect(() => {
    setState({
      physicianInterpretation,
      signature,
    });
  }, [isOpen]);

  return {
    state,
    onChangePhysicianInterpretation,
    onEndSignature,
    handleClick,
  };
};

export { useEditReportModal };
